import {
  resolveToolCallLabels,
  TerraUIMessageDataPartName,
  type TerraUIMessage,
  type ToolCallGenericMessage,
  type ToolCallLabels,
} from '@terra/shared-dto-chat-types';
import { toolCallIdOfPart } from './subagent-part-ownership';
import {
  openTargetFromTool,
  type SubagentOpenTarget,
} from './subagent-open-target';

type SubagentReasoningMetadata = {
  metadata?: {
    parentToolCallId?: string;
    reasoningStartedAt?: number;
    reasoningEndedAt?: number;
  };
};

export type SubagentActivityItem =
  | {
      kind: 'thought';
      id: string;
      text: string;
      isStreaming: boolean;
      providerMetadata?: SubagentReasoningMetadata;
    }
  | {
      kind: 'text';
      id: string;
      text: string;
      isStreaming: boolean;
    }
  | {
      kind: 'tool';
      id: string;
      toolName: string;
      dataMessage: ToolCallGenericMessage;
      labels: ToolCallLabels;
      detail: string;
      target: string;
      status: string;
      openTarget?: SubagentOpenTarget;
    };

const DETAIL_KEYS = [
  'file_path',
  'filePath',
  'path',
  'command',
  'query',
  'pattern',
  'question',
  'summary',
];

export function parentToolNameOf({
  parts,
  parentToolCallId,
}: {
  parts: NonNullable<TerraUIMessage['parts']>;
  parentToolCallId: string;
}): string | undefined {
  const parentPart = parts.find(
    (part) => toolCallIdOfPart(part) === parentToolCallId,
  );

  if (!parentPart || parentPart.type !== TerraUIMessageDataPartName) {
    return undefined;
  }

  const toolName = Reflect.get(parentPart.data, 'toolName');

  return typeof toolName === 'string' && toolName.trim()
    ? toolName.trim()
    : undefined;
}

export function textMatchesSubagentOrigin({
  part,
  parentToolName,
}: {
  part: NonNullable<TerraUIMessage['parts']>[number];
  parentToolName: string | undefined;
}): boolean {
  if (part.type !== 'text' || !parentToolName) {
    return false;
  }

  const origins = streamOriginsOfPart(part);

  if (origins.length === 0) {
    return false;
  }

  const parent = parentToolName.replace(/_/g, '-').toLowerCase();

  return origins.some((origin) => {
    const normalized = origin.replace(/_/g, '-').toLowerCase();

    return (
      normalized === `subagent::${parent}` || normalized.endsWith(`::${parent}`)
    );
  });
}

export function activityItem({
  part,
  index,
  idScope,
}: {
  part: NonNullable<TerraUIMessage['parts']>[number];
  index: number;
  idScope: string;
}): SubagentActivityItem | undefined {
  if (part.type === 'reasoning') {
    const text = part.text.trim();

    if (!text) {
      return undefined;
    }

    return {
      kind: 'thought',
      id: `${idScope}:thought-${index}`,
      text,
      isStreaming: part.state === 'streaming',
      providerMetadata: reasoningMetadataOf(part.providerMetadata),
    };
  }

  if (part.type === 'text') {
    const text = part.text.trim();

    if (!text) {
      return undefined;
    }

    return {
      kind: 'text',
      id: `${idScope}:text-${index}`,
      text,
      isStreaming: part.state === 'streaming',
    };
  }

  if (part.type !== TerraUIMessageDataPartName) {
    return undefined;
  }

  const data = part.data as {
    toolCallId?: string;
    toolName?: string;
    status?: string;
    message?: string;
    labelPresent?: string;
    labelPast?: string;
    input?: unknown;
    metadata?: { activity?: unknown };
  };

  if (!data.toolName || data.metadata?.activity === 'reasoning') {
    return undefined;
  }

  const dataMessage = part.data as ToolCallGenericMessage;
  const detailEntry = detailEntryFromInput(data.input);

  return {
    kind: 'tool',
    id: data.toolCallId ?? `tool-${index}`,
    toolName: data.toolName,
    dataMessage,
    labels: resolveToolCallLabels({
      toolName: data.toolName,
      message: data.message,
      labelPresent: data.labelPresent,
      labelPast: data.labelPast,
    }),
    detail: detailEntry ? `${detailEntry.key}: ${detailEntry.value}` : '',
    target: detailEntry?.value ?? '',
    status: data.status ?? 'completed',
    openTarget: data.toolName
      ? openTargetFromTool({ toolName: data.toolName, input: data.input })
      : undefined,
  };
}

function streamOriginsOfPart(
  part: NonNullable<TerraUIMessage['parts']>[number],
): string[] {
  if (part.type !== 'text' && part.type !== 'reasoning') {
    return [];
  }

  const metadata = part.providerMetadata?.metadata;

  if (!metadata || typeof metadata !== 'object') {
    return [];
  }

  const record = metadata as Record<string, unknown>;
  const origins: string[] = [];

  if (typeof record.terraStreamOrigin === 'string') {
    origins.push(record.terraStreamOrigin);
  }

  if (Array.isArray(record.tags)) {
    origins.push(
      ...record.tags.filter((tag): tag is string => typeof tag === 'string'),
    );
  }

  return origins;
}

function reasoningMetadataOf(
  providerMetadata: unknown,
): SubagentReasoningMetadata | undefined {
  if (!providerMetadata || typeof providerMetadata !== 'object') {
    return undefined;
  }

  const metadata = Reflect.get(providerMetadata, 'metadata');

  if (!metadata || typeof metadata !== 'object') {
    return { metadata: undefined };
  }

  const record = metadata as Record<string, unknown>;

  return {
    metadata: {
      ...(typeof record.parentToolCallId === 'string'
        ? { parentToolCallId: record.parentToolCallId }
        : {}),
      ...(typeof record.reasoningStartedAt === 'number'
        ? { reasoningStartedAt: record.reasoningStartedAt }
        : {}),
      ...(typeof record.reasoningEndedAt === 'number'
        ? { reasoningEndedAt: record.reasoningEndedAt }
        : {}),
    },
  };
}

function detailEntryFromInput(
  input: unknown,
): { key: string; value: string } | undefined {
  if (!input || typeof input !== 'object') {
    return undefined;
  }

  const record = input as Record<string, unknown>;
  const key = DETAIL_KEYS.find((candidate) => {
    const value = record[candidate];

    return typeof value === 'string' && value.trim().length > 0;
  });
  const value = key ? record[key] : undefined;

  return key && typeof value === 'string'
    ? { key, value: value.trim() }
    : undefined;
}
